import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  HealthAndSafety,
  TipsAndUpdates,
  Close,
  Info,
  Add,
  ArrowForwardIos,
} from '@mui/icons-material';
import { RouterLink as Link } from '../common/RouterLink';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useSymptoms } from '../../contexts/api-contexts/SymptomsContext';
import CaseSymptomSelectionDialog from './CaseSymptomSelectionDialog';

interface CaseSymptomsProps {
  onNavigate?: (path: string) => void;
  onTabChange?: (
    tab: 'basic' | 'symptoms' | 'diagnoses' | 'treatments'
  ) => void;
}

const CaseSymptoms: React.FC<CaseSymptomsProps> = ({
  onNavigate: _onNavigate,
  onTabChange,
}) => {
  const { currentCase, setCaseProperty, persistCase } = useCases();
  const {
    fetchSymptoms,
    symptoms,
    isLoading: isLoadingSymptoms,
    setSymptoms,
  } = useSymptoms();

  // State for symptom selection dialog
  const [symptomDialogOpen, setSymptomDialogOpen] = useState(false);

  // Get all unique symptom IDs that we need to fetch
  const allSymptomIds = useMemo(() => {
    const ids = new Set<number>();

    // Add selected symptom IDs
    currentCase?.selected_symptoms?.forEach((symptom) => ids.add(symptom.id));

    // Add suggested symptom IDs
    currentCase?.suggested_symptoms?.forEach((item) =>
      ids.add(item.symptom.id)
    );

    return Array.from(ids);
  }, [currentCase?.selected_symptoms, currentCase?.suggested_symptoms]);

  // Fetch symptoms when IDs change
  useEffect(() => {
    if (allSymptomIds.length > 0) {
      fetchSymptoms({ ids: allSymptomIds.join(',') });
    } else {
      setSymptoms([]);
    }
  }, [allSymptomIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to get symptom name by ID
  const getSymptomName = useCallback(
    (symptomId: number): [string, string] => {
      const symptom = symptoms.find((s) => s.id === symptomId);
      let name = '';
      if (symptom?.parent_symptom) {
        name = `${symptom.parent_symptom.name}, `;
      }
      name += symptom?.name || `Symptom ${symptomId}`;

      let typeName = '';
      if (symptom?.symptom_type) {
        typeName = `${symptom.symptom_type.category.name}, ${symptom.symptom_type.name}`;
      }
      return [name, typeName];
    },
    [symptoms]
  );

  // Handle symptom deselection
  const handleDeselectSymptom = useCallback(
    async (symptomId: number) => {
      if (!currentCase) return;

      const updatedSelectedSymptoms =
        currentCase.selected_symptoms?.filter(
          (symptom) => symptom.id !== symptomId
        ) || [];

      const updatedDeSelectedSymptoms = [
        ...(currentCase.de_selected_symptoms || []),
        symptomId,
      ];

      setCaseProperty('selected_symptoms', updatedSelectedSymptoms);
      setCaseProperty('de_selected_symptoms', updatedDeSelectedSymptoms);

      // Persist the case after updating symptoms
      try {
        await persistCase();
      } catch (error) {
        console.error(
          'Error persisting case after deselecting symptom:',
          error
        );
      }
    },
    [currentCase, setCaseProperty, persistCase]
  );

  // Handle adding suggested symptom to selected list
  const handleAddSymptom = useCallback(
    async (symptomId: number) => {
      if (!currentCase) return;

      const updatedSelectedSymptoms = [
        ...(currentCase.selected_symptoms || []),
        { id: symptomId, significant: false },
      ];

      const updatedDeSelectedSymptoms =
        currentCase.de_selected_symptoms?.filter((id) => id !== symptomId) ||
        [];

      setCaseProperty('selected_symptoms', updatedSelectedSymptoms);
      setCaseProperty('de_selected_symptoms', updatedDeSelectedSymptoms);

      // Persist the case after updating symptoms
      try {
        await persistCase();
      } catch (error) {
        console.error('Error persisting case after adding symptom:', error);
      }
    },
    [currentCase, setCaseProperty, persistCase]
  );

  // Convert selected symptoms to display format
  const displaySelectedSymptoms = useMemo(() => {
    if (!currentCase?.selected_symptoms) return [];

    return currentCase.selected_symptoms.map((symptom) => {
      // Find corresponding suggested symptom to get textSnippets
      const suggestedSymptom = currentCase.suggested_symptoms?.find(
        (suggested) => suggested.symptom.id === symptom.id
      );

      return {
        id: symptom.id,
        name: getSymptomName(symptom.id),
        textSnippets: suggestedSymptom?.textSnippets || [],
      };
    });
  }, [
    currentCase?.selected_symptoms,
    currentCase?.suggested_symptoms,
    getSymptomName,
  ]);

  // Convert suggested symptoms to display format (including already selected with indicator)
  const displaySuggestedSymptoms = useMemo(() => {
    const selectedSymptomIds = new Set(
      currentCase?.selected_symptoms?.map((s) => s.id) || []
    );

    return (
      currentCase?.suggested_symptoms?.map((item) => ({
        id: item.symptom.id,
        name: getSymptomName(item.symptom.id),
        keyPhrases: item.keyPhrases,
        textSnippets: item.textSnippets,
        isSelected: selectedSymptomIds.has(item.symptom.id),
      })) || []
    );
  }, [
    currentCase?.suggested_symptoms,
    currentCase?.selected_symptoms,
    getSymptomName,
  ]);

  return (
    <Card
      elevation={1}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <CardContent sx={{ flexGrow: 1, p: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HealthAndSafety sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
            Case Symptoms
          </Typography>
        </Box>

        {/* Selected Symptoms Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
          >
            Selected Symptoms ({displaySelectedSymptoms.length})
            {isLoadingSymptoms && (
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, color: 'text.secondary' }}
              >
                (Loading names...)
              </Typography>
            )}
          </Typography>
          {displaySelectedSymptoms.length > 0 ? (
            <List
              dense
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {displaySelectedSymptoms.map((symptom) => (
                <ListItem key={symptom.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={symptom.name[0]}
                    secondary={symptom.name[1]}
                    primaryTypographyProps={{
                      variant: 'body1',
                    }}
                  />
                  <ListItemSecondaryAction
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {/* Info icon for AI-suggested symptoms with textSnippets */}
                    {symptom.textSnippets &&
                      symptom.textSnippets.length > 0 && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, mb: 1 }}
                              >
                                AI Reasoning:
                              </Typography>
                              <Typography variant="body1">
                                {symptom.textSnippets.join(' | ')}
                              </Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    <IconButton
                      edge="end"
                      aria-label="deselect"
                      onClick={() => handleDeselectSymptom(symptom.id)}
                      size="small"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}

              {/* ALTERNATIVE 3: Minimal Style with Plus Icon on Left (Active) */}
              <ListItem
                onClick={() => {
                  setSymptomDialogOpen(true);
                }}
                sx={{
                  py: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                  },
                }}
              >
                <Add sx={{ mr: 2, color: 'text.secondary', fontSize: 20 }} />
                <ListItemText
                  primary="Add symptom"
                  secondary="Browse symptom tree or search"
                  primaryTypographyProps={{
                    variant: 'body1',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                  secondaryTypographyProps={{
                    color: 'text.secondary',
                  }}
                />
              </ListItem>

              {/* ALTERNATIVE 1: Dashed Border Style */}
              {/* 
              <ListItem
                button
                onClick={() => {
                  // TODO: Open symptom selection dialog
                  console.log('Open symptom selection dialog');
                }}
                sx={{
                  py: 0.5,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  mx: 1,
                  mb: 1,
                  mt: 0.5,
                  '&:hover': {
                    bgcolor: 'primary.50',
                    borderColor: 'primary.dark',
                  },
                }}
              >
                <Add sx={{ mr: 2, color: 'primary.main' }} />
                <ListItemText
                  primary="Add symptom"
                  secondary="Browse symptom tree or search"
                  primaryTypographyProps={{
                    variant: 'body1',
                    color: 'primary.main',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    color: 'primary.main',
                    opacity: 0.7,
                  }}
                />
              </ListItem>
              */}

              {/* ALTERNATIVE 2: Subtle Background Style */}
              {/* 
              <ListItem
                button
                onClick={() => {
                  // TODO: Open symptom selection dialog
                  console.log('Open symptom selection dialog');
                }}
                sx={{
                  py: 0.5,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  mx: 1,
                  mb: 1,
                  mt: 0.5,
                  '&:hover': {
                    bgcolor: 'primary.50',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <ListItemText
                  primary="Add symptom"
                  secondary="Browse symptom tree or search"
                  primaryTypographyProps={{
                    variant: 'body1',
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    color: 'text.secondary',
                    opacity: 0.8,
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    sx={{ 
                      color: 'primary.main',
                      bgcolor: 'primary.50',
                      '&:hover': {
                        bgcolor: 'primary.100',
                      },
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              */}
            </List>
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              No selected symptoms found for this case.
            </Alert>
          )}

          {/* Navigation link to Diagnoses - only show if symptoms are selected */}
          {displaySelectedSymptoms.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  displaySelectedSymptoms.length === 0
                    ? 'Select symptoms to continue to diagnoses'
                    : ''
                }
              >
                <span>
                  <Link
                    to={`/cases/${currentCase?.id}?tab=diagnoses`}
                    style={{
                      textDecoration: 'none',
                      pointerEvents:
                        displaySelectedSymptoms.length === 0 ? 'none' : 'auto',
                      opacity: displaySelectedSymptoms.length === 0 ? 0.5 : 1,
                    }}
                    onClick={(e) => {
                      if (displaySelectedSymptoms.length === 0) {
                        e.preventDefault();
                        return;
                      }
                      e.preventDefault();
                      onTabChange?.('diagnoses');
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontSize: '1.1rem',
                        fontWeight: 'medium',
                        '&:hover': {
                          color: 'primary.dark',
                        },
                      }}
                    >
                      Continue to Diagnoses
                      <ArrowForwardIos sx={{ fontSize: '1rem' }} />
                    </Box>
                  </Link>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Suggested Symptoms Section */}
        {displaySuggestedSymptoms.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TipsAndUpdates
                sx={{ mr: 1, color: 'warning.main', fontSize: 18 }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                AI Suggested Symptoms ({displaySuggestedSymptoms.length})
                {isLoadingSymptoms && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    (Loading names...)
                  </Typography>
                )}
              </Typography>
            </Box>
            <List
              dense
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {displaySuggestedSymptoms.map((symptom) => (
                <ListItem
                  key={symptom.id}
                  sx={{
                    py: 0.5,
                    bgcolor: symptom.isSelected ? 'action.selected' : 'inherit',
                    opacity: symptom.isSelected ? 0.7 : 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {symptom.name[0]}
                        {symptom.isSelected && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              bgcolor: 'success.light',
                              color: 'success.contrastText',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          >
                            Selected
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={symptom.name[1]}
                    primaryTypographyProps={{
                      variant: 'body1',
                      component: 'div',
                    }}
                  />
                  <ListItemSecondaryAction
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {/* Info icon for AI-suggested symptoms with textSnippets */}
                    {symptom.textSnippets &&
                      symptom.textSnippets.length > 0 && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, mb: 1 }}
                              >
                                AI Reasoning:
                              </Typography>
                              <Typography variant="body1">
                                {symptom.textSnippets.join(' | ')}
                              </Typography>
                              {symptom.keyPhrases &&
                                symptom.keyPhrases.length > 0 && (
                                  <>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}
                                    >
                                      Key Phrases:
                                    </Typography>
                                    <Typography variant="body1">
                                      {symptom.keyPhrases.join(', ')}
                                    </Typography>
                                  </>
                                )}
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    {!symptom.isSelected && (
                      <IconButton
                        edge="end"
                        aria-label="add symptom"
                        onClick={() => handleAddSymptom(symptom.id)}
                        size="small"
                        sx={{ color: 'success.main' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* No Symptoms Message */}
        {displaySelectedSymptoms.length === 0 &&
          displaySuggestedSymptoms.length === 0 && (
            <Alert severity="info">
              No symptoms data available for this case.
            </Alert>
          )}
      </CardContent>

      {/* Symptom Selection Dialog */}
      <CaseSymptomSelectionDialog
        open={symptomDialogOpen}
        onClose={() => setSymptomDialogOpen(false)}
      />
    </Card>
  );
};

export default CaseSymptoms;
